const pad2 = (n) => `${n}`.padStart(2, '0')
const toYMD = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`

const MS_PER_DAY = 24 * 60 * 60 * 1000
const MS_PER_HALF_DAY = MS_PER_DAY / 2

// 엑셀 날짜 일련번호의 기준일(1899-12-30)과 상한(2958465 = 9999-12-31).
// 상한 덕분에 일련번호와 사용자가 직접 친 yyyyMMdd(20240315 등)를 구분할 수 있다.
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30)
const EXCEL_SERIAL_MAX = 2958465

/**
 * 사람이 입력한 날짜 문자열을 [연, 월, 일] 로 나눈다.
 * 2024-03-15, 2024.03.15, 2024/03/15, "2024. 3. 15", 2024년 3월 15일, 20240315 을 모두 받는다.
 * @param text 원본 문자열
 * @returns {number[]|null} [연, 월, 일]. 형태가 맞지 않으면 null
 */
const splitYMD = (text) => {
  const parts = text.match(/\d+/g) || []

  if (parts.length === 3 && parts[0].length === 4 && parts[1].length <= 2 && parts[2].length <= 2) {
    return parts.map(Number)
  }
  if (parts.length === 1 && parts[0].length === 8) {
    return [parts[0].slice(0, 4), parts[0].slice(4, 6), parts[0].slice(6, 8)].map(Number)
  }
  return null
}

class DateUtil {
  /**
   * 엑셀 셀에서 읽어온 값을 yyyy-MM-dd 형태로 변환한다.
   *
   * xlsx 0.15 는 날짜 셀의 일련번호를 Date 로 되돌릴 때 브라우저 타임존으로 보정하는데,
   * 이 보정값이 분 단위로 반올림되어 자정에서 어긋난 시각이 만들어진다.
   * (Asia/Seoul 은 1899-12-30 의 오프셋 +8:27:52 를 33분으로 어림잡아 52초가 남는다.
   *  그대로 날짜만 취하면 하루 앞당겨지고, 반대로 서머타임 시행 기간의 날짜는
   *  시계가 한 시간 앞서 있어 오차 없이 읽힌다.)
   * 어긋나는 폭이 한 시간을 넘지 않으므로 가장 가까운 자정으로 맞춘 뒤 날짜를 취한다.
   *
   * @param value 엑셀 셀 값 (Date | number | string)
   * @returns {string} 변환된 날짜 (yyyy-MM-dd). 날짜로 해석할 수 없으면 빈 문자열
   */
  static excelCellToYYYYMMDD (value) {
    if (value === null || value === undefined || value === '') return ''

    // 날짜 서식 셀 : cellDates 옵션으로 Date 객체가 넘어온다
    if (value instanceof Date) {
      if (isNaN(value.getTime())) return ''
      const nearestNoon = new Date(value.getTime() + MS_PER_HALF_DAY)
      return toYMD(nearestNoon.getFullYear(), nearestNoon.getMonth() + 1, nearestNoon.getDate())
    }

    // 날짜 서식 셀 : cellDates 옵션이 없으면 일련번호(기준일로부터의 일수)가 넘어온다.
    // UTC 로만 계산하므로 타임존의 영향을 받지 않는다.
    if (typeof value === 'number' && 0 < value && value <= EXCEL_SERIAL_MAX) {
      const parsed = new Date(EXCEL_EPOCH_UTC + (Math.round(value) * MS_PER_DAY))
      return toYMD(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, parsed.getUTCDate())
    }

    // 사람이 직접 입력한 값
    const ymd = splitYMD(`${value}`)
    if (!ymd) return ''

    const [y, m, d] = ymd

    // 2024-13-01, 2024-02-31 같은 값을 걸러낸다
    const check = new Date(Date.UTC(y, m - 1, d))
    if (check.getUTCFullYear() !== y || check.getUTCMonth() + 1 !== m || check.getUTCDate() !== d) return ''

    return toYMD(y, m, d)
  }

  /**
   * timestamp를 yyyy-MM-dd 형태로 변환한다.
   * @param unix_timestamp
   * @returns {string} 변환된 날짜 데이터 (yyyy-MM-dd)
   */
  static convertTimestampToYYYYMMDD (unix_timestamp) {
    unix_timestamp = Number(unix_timestamp)
    const dateTime = new Date(unix_timestamp * 1000)
    const currentTime = new Date()

    const yyyy = dateTime.getFullYear()
    let MM = (dateTime.getMonth() + 1)
    let dd = dateTime.getDate()

    MM = (MM > 9) ? MM : `0${MM}`
    dd = (dd > 9) ? dd : `0${dd}`

    if (dateTime.getFullYear() === currentTime.getFullYear()
      && dateTime.getMonth() === currentTime.getMonth()
      && dateTime.getDate() === currentTime.getDate()) {
      return `${dateTime.getHours()  } : ${  dateTime.getMinutes()}`
    } else {
      return `${yyyy  }-${  MM  }-${  dd}`
    }
  }
}

export default DateUtil
